import api, { type PaginatedResponse } from "./api";

export interface RoleSummary {
  id: string;
  name: string;
  displayName?: string | null;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  role?: RoleSummary;
  roleId?: string;
  additionalRoles?: RoleSummary[];
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface Role {
  id: string;
  name: string;
}

export interface UserRole {
  id: string;
  roleId: string;
  roleName: string;
  roleDisplayName?: string | null;
  assignedAt: string;
  assignedByName: string | null;
}

export interface UserInvite {
  email: string;
  roleId: string;
  firstName?: string | undefined;
  lastName?: string | undefined;
  message?: string | undefined;
}

export interface Invitation {
  id: string;
  email: string;
  status: string;
  firstName: string | null;
  lastName: string | null;
  invitedAt: string;
  expiresAt: string;
}

export interface InvitationDetail extends Invitation {
  roleId: string;
  roleName: string | null;
  roleDisplayName?: string | null;
  token: string;
  inviteLink: string;
  message: string | null;
  resendCount: number;
  invitedByName: string | null;
}

export interface ApproveInvitation {
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface UserUpdate {
  firstName?: string;
  lastName?: string;
  status?: string;
  roleId?: string;
}

export interface BatchInviteItem {
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface BatchInviteRequest {
  emailsText?: string;
  items?: BatchInviteItem[];
  roleId: string;
  message?: string;
}

export interface BatchInviteResultItem {
  email: string;
  status: "invited" | "skipped" | "error";
  reason?: string | null;
  invitationId?: string | null;
}

export interface BatchInviteResponse {
  total: number;
  invited: number;
  skipped: number;
  errors: number;
  results: BatchInviteResultItem[];
}

export interface UserCompanyMembership {
  id?: string;
  companyId: string;
  companyName: string;
  parentId: string | null;
  isRoot: boolean;
  roleId: string;
  roleName: string;
  roleDisplayName?: string | null;
  assignedAt: string;
  assignedByName?: string | null;
}

export interface AddUserToCompany {
  companyId: string;
}

export interface UserGroupMembership {
  id: string;
  groupId: string;
  groupName: string;
  groupDescription: string | null;
  assignedAt: string;
  assignedByName?: string | null;
}

export interface AddUserToGroup {
  groupId: string;
}

export const usersService = {
  async list(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    roleId?: string;
    search?: string;
    sortBy?: "created_at" | "name" | "email";
    sortOrder?: "asc" | "desc";
  }): Promise<PaginatedResponse<User>> {
    return api.get<PaginatedResponse<User>>("/v1/users", params);
  },

  // Lean list for assignment pickers — works with users:read-assignable, so
  // attendants without full user management can still assign work. Extra params
  // (pageSize/sort/status) are accepted and ignored server-side; only `search`
  // is applied.
  async listAssignable(params?: Record<string, unknown>): Promise<{ items: User[] }> {
    return api.get<{ items: User[] }>("/v1/users/assignable", params);
  },

  async get(userId: string): Promise<User> {
    return api.get<User>(`/v1/users/${userId}`);
  },

  async update(userId: string, data: UserUpdate): Promise<User> {
    return api.put<User>(`/v1/users/${userId}`, data);
  },

  async remove(userId: string): Promise<void> {
    return api.delete(`/v1/users/${userId}`);
  },

  // Admin password actions (users:manage)
  async sendPasswordReset(userId: string): Promise<{ sent: boolean; email: string }> {
    return api.post(`/v1/users/${userId}/password-reset/send`);
  },

  async createPasswordResetLink(
    userId: string,
    params?: { expiresHours?: number },
  ): Promise<{ url: string; expiresAt: string }> {
    return api.post(`/v1/users/${userId}/password-reset/link`, params ?? {});
  },

  async setPassword(userId: string, params: { password: string }): Promise<{ updated: boolean }> {
    return api.post(`/v1/users/${userId}/password`, params);
  },

  // Suspend / reactivate (users:manage) — instant, nothing is deleted
  async suspend(userId: string, params?: { reason?: string }): Promise<{ status: string }> {
    return api.post(`/v1/users/${userId}/suspend`, params ?? {});
  },

  async reactivate(userId: string): Promise<{ status: string }> {
    return api.post(`/v1/users/${userId}/reactivate`);
  },

  async updateEmail(userId: string, email: string): Promise<User> {
    return api.patch<User>(`/v1/users/${userId}/email`, { email });
  },

  async invite(data: UserInvite): Promise<Invitation> {
    return api.post<Invitation>("/v1/users/invite", data);
  },

  async batchInvite(data: BatchInviteRequest): Promise<BatchInviteResponse> {
    return api.post<BatchInviteResponse>("/v1/users/invite/batch", data);
  },

  async batchInviteUpload(file: File, roleId: string, message?: string): Promise<BatchInviteResponse> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("role_id", roleId);
    if (message) {
      formData.append("message", message);
    }
    return api.upload<BatchInviteResponse>("/v1/users/invite/batch/upload", formData);
  },

  async resendInvitation(invitationId: string): Promise<Invitation> {
    return api.post<Invitation>(`/v1/users/invite/${invitationId}/resend`);
  },

  async listInvitations(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
  }): Promise<PaginatedResponse<Invitation>> {
    return api.get<PaginatedResponse<Invitation>>("/v1/users/invitations", params);
  },

  async getInvitation(invitationId: string): Promise<InvitationDetail> {
    return api.get<InvitationDetail>(`/v1/users/invitations/${invitationId}`);
  },

  async approveInvitation(invitationId: string, data: ApproveInvitation): Promise<User> {
    return api.post<User>(`/v1/users/invitations/${invitationId}/approve`, data);
  },

  async cancelInvitation(invitationId: string): Promise<void> {
    return api.delete(`/v1/users/invitations/${invitationId}`);
  },

  async validateInviteToken(token: string): Promise<{
    valid: boolean;
    email: string;
    firstName: string | null;
    lastName: string | null;
  }> {
    return api.get(`/v1/users/invite/${token}/validate`);
  },

  async acceptInvitation(token: string, data: {
    password: string;
    confirmPassword: string;
    firstName?: string | undefined;
    lastName?: string | undefined;
  }): Promise<User> {
    return api.post<User>(`/v1/users/invite/${token}/accept`, data);
  },

  async getUserCompanies(
    userId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<UserCompanyMembership>> {
    return api.get<PaginatedResponse<UserCompanyMembership>>(
      `/v1/users/${userId}/companies`,
      params
    );
  },

  async addUserToCompany(userId: string, data: AddUserToCompany): Promise<UserCompanyMembership> {
    return api.post<UserCompanyMembership>(`/v1/users/${userId}/companies`, data);
  },

  async removeUserFromCompany(userId: string, companyId: string): Promise<void> {
    return api.delete(`/v1/users/${userId}/companies/${companyId}`);
  },

  // User Roles (Additional Roles)
  async listUserRoles(
    userId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<UserRole>> {
    return api.get<PaginatedResponse<UserRole>>(
      `/v1/users/${userId}/roles`,
      params
    );
  },

  async addUserRole(userId: string, roleId: string): Promise<UserRole> {
    return api.post<UserRole>(`/v1/users/${userId}/roles`, { roleId });
  },

  async removeUserRole(userId: string, roleId: string): Promise<void> {
    return api.delete(`/v1/users/${userId}/roles/${roleId}`);
  },

  // User Groups
  async getUserGroups(
    userId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<UserGroupMembership>> {
    return api.get<PaginatedResponse<UserGroupMembership>>(
      `/v1/users/${userId}/groups`,
      params
    );
  },

  async addUserToGroup(userId: string, data: AddUserToGroup): Promise<UserGroupMembership> {
    return api.post<UserGroupMembership>(`/v1/users/${userId}/groups`, data);
  },

  async removeUserFromGroup(userId: string, groupId: string): Promise<void> {
    return api.delete(`/v1/users/${userId}/groups/${groupId}`);
  },

  // User Company Roles (roles per company)
  async listUserCompanyRoles(
    userId: string,
    companyId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<UserRole>> {
    return api.get<PaginatedResponse<UserRole>>(
      `/v1/users/${userId}/companies/${companyId}/roles`,
      params
    );
  },

  async addUserCompanyRole(userId: string, companyId: string, roleId: string): Promise<UserRole> {
    return api.post<UserRole>(`/v1/users/${userId}/companies/${companyId}/roles`, { roleId });
  },

  async removeUserCompanyRole(userId: string, companyId: string, roleId: string): Promise<void> {
    return api.delete(`/v1/users/${userId}/companies/${companyId}/roles/${roleId}`);
  },

  // Tour State
  async getTourState(): Promise<{ tourState: Record<string, any> }> {
    return api.get("/v1/users/me/tour-state");
  },

  async updateTourState(tourState: Record<string, any>): Promise<{ tourState: Record<string, any> }> {
    return api.patch("/v1/users/me/tour-state", { tourState });
  },

  async resetMyTours(): Promise<{ message: string }> {
    return api.delete("/v1/users/me/tour-state");
  },

  async resetUserTours(userId: string): Promise<{ message: string }> {
    return api.delete(`/v1/users/${userId}/tour-state`);
  },

  async resetAllTours(): Promise<{ message: string; count: number }> {
    return api.post("/v1/users/tour-state/reset-all");
  },
};

export default usersService;
